import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  Check,
  ChevronRight,
  Package,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  X,
  Zap,
} from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { formatPrice } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { haptic } from '@/lib/telegram'

export interface CapsuleProduct {
  id: number
  slug: string
  name_uk: string
  name_en: string
  brand: string
  price_uah: number
  decant_price_uah: number
  photo_url?: string | null
  vector: Record<string, number>
  dominant_chords: string[]
  key_notes: string[]
  match_pct: number
  distance: number
}

export interface CapsuleOccasion {
  id: string
  icon: string
  name_uk: string
  name_en: string
  name_ru: string
  desc_uk: string
  desc_en: string
  desc_ru: string
  color: string
  vector: Record<string, number>
  candidates: CapsuleProduct[]
}

export interface CapsuleResponse {
  occasions: CapsuleOccasion[]
  default_capsule: Array<{ occasion_id: string; product: CapsuleProduct }>
  archetypes: Array<{
    id: string
    name_uk: string
    name_en: string
    name_ru: string
    desc_uk: string
    desc_en: string
    desc_ru: string
    icon: string
  }>
  diversity: {
    score: number
    label_uk: string
    label_en: string
    label_ru: string
    avg_distance: number
    min_distance: number
  }
  pricing: {
    volume_ml: string
    regular_total_uah: number
    bundle_price_uah: number
    discount_pct: number
    savings_uah: number
  }
}

export function ScentCapsuleBuilder() {
  const lang = useAppStore((s) => s.lang)
  const setCartCount = useAppStore((s) => s.setCartCount)
  const qc = useQueryClient()
  const navigate = useNavigate()

  const [activeReplacingOccasion, setActiveReplacingOccasion] = useState<string | null>(null)
  const [candidateSearch, setCandidateSearch] = useState('')
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [cartSuccessOpen, setCartSuccessOpen] = useState(false)
  const [activeArchetype, setActiveArchetype] = useState<string | null>('aristocrat')

  // Fetch full capsule data from backend
  const { data, isLoading } = useQuery<CapsuleResponse>({
    queryKey: ['scent-capsule-catalog'],
    queryFn: () => api.get<CapsuleResponse>('/scent-finder/capsule'),
    staleTime: 5 * 60_000,
  })

  // Selected products mapped by occasion ID (day, evening, cozy, active)
  const [selectedMap, setSelectedMap] = useState<Record<string, CapsuleProduct>>({})

  const defaultCapsule = data?.default_capsule

  // Initialize selectedMap once data loads
  const currentSelected = useMemo(() => {
    if (Object.keys(selectedMap).length > 0) return selectedMap
    if (!defaultCapsule) return {}
    const initial: Record<string, CapsuleProduct> = {}
    defaultCapsule.forEach((item) => {
      initial[item.occasion_id] = item.product
    })
    return initial
  }, [selectedMap, defaultCapsule])

  // Dynamic diversity calculation based on the 4 active choices
  const currentDiversity = useMemo(() => {
    const prods = Object.values(currentSelected)
    if (prods.length < 2) {
      return {
        score: 100,
        label_uk: 'Бездоганний баланс',
        label_en: 'Flawless balance',
        label_ru: 'Безупречный баланс',
      }
    }
    const axes = ['freshness', 'sharpness', 'sillage', 'sweetness', 'formality']
    const dists: number[] = []
    for (let i = 0; i < prods.length; i++) {
      for (let j = i + 1; j < prods.length; j++) {
        const v1 = prods[i].vector
        const v2 = prods[j].vector
        const d = Math.hypot(...axes.map((a) => (v1[a] ?? 0.5) - (v2[a] ?? 0.5)))
        dists.push(d)
      }
    }
    const avgD = dists.reduce((a, b) => a + b, 0) / dists.length
    const score = Math.min(100, Math.max(25, Math.round((avgD / 0.62) * 100)))

    let label_uk = 'Бездоганний баланс (0% дублювання)'
    let label_en = 'Flawless Balance (0% redundancy)'
    let label_ru = 'Безупречный баланс (0% дублирования)'

    if (score < 70) {
      label_uk = 'Є подібні аромати — спробуйте контраст'
      label_en = 'Some scents overlap — try more contrast'
      label_ru = 'Есть похожие ароматы — добавьте контраст'
    } else if (score < 88) {
      label_uk = 'Гармонійний ольфакторний гардероб'
      label_en = 'Harmonious Olfactory Wardrobe'
      label_ru = 'Гармоничный ольфакторный гардероб'
    }

    return { score, label_uk, label_en, label_ru, avgD }
  }, [currentSelected])

  // Price calculations
  const { regularTotal, bundlePrice, savings } = useMemo(() => {
    const prods = Object.values(currentSelected)
    const regular = prods.reduce((sum, p) => sum + (p.decant_price_uah || 220), 0)
    const discounted = Math.round(regular * 0.85)
    return {
      regularTotal: regular || 1200,
      bundlePrice: discounted || 1020,
      savings: regular - discounted,
    }
  }, [currentSelected])

  // Handle slot replacement
  const handleSelectProductForSlot = (occasionId: string, product: CapsuleProduct) => {
    haptic('medium')
    setSelectedMap((prev) => ({
      ...prev,
      [occasionId]: product,
    }))
    setActiveReplacingOccasion(null)
    setCandidateSearch('')
    setActiveArchetype(null)
  }

  // Handle archetype preset loading
  const handleLoadArchetype = (archId: string) => {
    if (!data?.occasions) return
    haptic('medium')
    setActiveArchetype(archId)

    // Curated index offset for distinct archetype vibes
    const offsetMap: Record<string, number> = {
      aristocrat: 0,
      clean_minimal: 1,
      dark_seduction: 2,
      mediterranean: 3,
    }
    const offset = offsetMap[archId] ?? 0

    const updated: Record<string, CapsuleProduct> = {}
    data.occasions.forEach((occ, idx) => {
      const candIdx = (offset + idx) % occ.candidates.length
      updated[occ.id] = occ.candidates[candIdx] || occ.candidates[0]
    })
    setSelectedMap(updated)
  }

  // Handle 1-click Discovery Box order (Batch Add to Cart)
  const handleOrderDiscoveryBox = async () => {
    const productsToOrder = Object.values(currentSelected)
    if (productsToOrder.length === 0) return

    setIsAddingToCart(true)
    haptic('medium')

    try {
      const items = productsToOrder.map((p) => ({
        product_id: p.id,
        qty: 1,
      }))

      // Try batch add endpoint first, fallback to individual cart add
      try {
        const res = await api.post<{ status: string; count: number }>('/cart/add-batch', { items })
        setCartCount(res.count)
      } catch (innerErr) {
        if (innerErr instanceof ApiError && innerErr.status === 401) {
          void navigate({ to: '/login' })
          return
        }
        for (const item of items) {
          await api.post('/cart/add', item)
        }
        const cart = await api.get<{ count: number }>('/cart')
        setCartCount(cart.count)
      }

      qc.invalidateQueries({ queryKey: ['cart'] })
      haptic('success')
      setCartSuccessOpen(true)
    } catch (err) {
      haptic('error')
      if (err instanceof ApiError && err.status === 401) {
        void navigate({ to: '/login' })
        return
      }
      alert(
        lang === 'en'
          ? 'Failed to add Discovery Box to cart'
          : lang === 'ru'
            ? 'Не удалось добавить Discovery Box в корзину'
            : 'Не вдалося додати Discovery Box до кошика',
      )
    } finally {
      setIsAddingToCart(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 space-y-3 bg-[#181622] rounded-2xl border border-white/10">
        <RefreshCw className="w-6 h-6 text-[#d7b171] animate-spin" />
        <span className="text-xs text-neutral-400 font-medium">
          {lang === 'en'
            ? 'Analyzing 5D sensory space & constructing wardrobe capsule...'
            : lang === 'ru'
              ? 'Анализ сенсорного пространства и построение гардероба...'
              : 'Аналізуємо 5D сенсорний простір та формуємо парфумерний гардероб...'}
        </span>
      </div>
    )
  }

  const occasions = data?.occasions || []
  const archetypes = data?.archetypes || []
  const activeOccasionData = occasions.find((o) => o.id === activeReplacingOccasion)

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* ── 1. VALUE HIGHLIGHTS BAR (CLEAN & NON-DUPLICATIVE) ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <div className="p-3 rounded-2xl bg-white/[0.03] border border-white/10 flex items-start gap-2.5 shadow-md">
          <div className="w-8 h-8 rounded-xl bg-[#c8a96e]/15 border border-[#c8a96e]/30 flex items-center justify-center shrink-0">
            <span className="text-sm">👔</span>
          </div>
          <div className="min-w-0">
            <span className="text-xs font-bold text-white block">
              {lang === 'en' ? '4 Occasions' : lang === 'ru' ? '4 Ситуации' : '4 Ситуації'}
            </span>
            <span className="text-[10px] text-neutral-400 block leading-tight mt-0.5">
              {lang === 'en' ? 'Office, evening, cozy, active' : lang === 'ru' ? 'Офис, вечер, уют, спорт' : 'Офіс, вечір, затишок, спорт'}
            </span>
          </div>
        </div>
        <div className="p-3 rounded-2xl bg-white/[0.03] border border-white/10 flex items-start gap-2.5 shadow-md">
          <div className="w-8 h-8 rounded-xl bg-[#c8a96e]/15 border border-[#c8a96e]/30 flex items-center justify-center shrink-0">
            <span className="text-sm">🧪</span>
          </div>
          <div className="min-w-0">
            <span className="text-xs font-bold text-white block">
              {lang === 'en' ? '20 ml Total' : lang === 'ru' ? '20 мл Ниши' : '20 мл Ніші'}
            </span>
            <span className="text-[10px] text-neutral-400 block leading-tight mt-0.5">
              {lang === 'en' ? '4 x 5 ml spray decants' : lang === 'ru' ? '4 атомайзера по 5 мл' : '4 атомайзери по 5 мл'}
            </span>
          </div>
        </div>
        <div className="p-3 rounded-2xl bg-white/[0.03] border border-white/10 flex items-start gap-2.5 shadow-md">
          <div className="w-8 h-8 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <span className="text-sm">✨</span>
          </div>
          <div className="min-w-0">
            <span className="text-xs font-bold text-emerald-400 block">
              {lang === 'en' ? '0% Overlap' : lang === 'ru' ? '0% Дубликатов' : '0% Дублювання'}
            </span>
            <span className="text-[10px] text-neutral-400 block leading-tight mt-0.5">
              {lang === 'en' ? '100% note orthogonality' : lang === 'ru' ? 'Полный баланс нот' : 'Повна ортогональність нот'}
            </span>
          </div>
        </div>
        <div className="p-3 rounded-2xl bg-white/[0.03] border border-white/10 flex items-start gap-2.5 shadow-md">
          <div className="w-8 h-8 rounded-xl bg-[#c8a96e]/20 border border-[#c8a96e]/40 flex items-center justify-center shrink-0">
            <span className="text-sm">🏷️</span>
          </div>
          <div className="min-w-0">
            <span className="text-xs font-bold text-[#ebd5ab] block">
              {lang === 'en' ? '-15% Bundle Savings' : lang === 'ru' ? '-15% Скидка' : '-15% Знижка'}
            </span>
            <span className="text-[10px] text-neutral-400 block leading-tight mt-0.5">
              {lang === 'en' ? 'In Discovery Box' : lang === 'ru' ? 'В Discovery Box' : 'У наборі Discovery Box'}
            </span>
          </div>
        </div>
      </div>

      {/* ── 2. ARCHETYPE PRESETS BAR ── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs px-1">
          <span className="font-semibold uppercase tracking-wider text-[#d7b171] text-[11px] flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            <span>
              {lang === 'en'
                ? 'Curated Archetype Capsules'
                : lang === 'ru'
                  ? 'Готовые архетипы гардероба'
                  : 'Готові архетипи гардеробу (в 1 клік)'}
            </span>
          </span>
          <span className="text-[11px] text-neutral-400">
            {lang === 'en' ? '1-click load' : lang === 'ru' ? 'В 1 клик' : 'В 1 клік'}
          </span>
        </div>
        <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-none snap-x 2xl:grid 2xl:grid-cols-4">
          {archetypes.map((arch) => {
            const isActive = activeArchetype === arch.id
            const name = lang === 'en' ? arch.name_en : lang === 'ru' ? arch.name_ru : arch.name_uk
            const desc = lang === 'en' ? arch.desc_en : lang === 'ru' ? arch.desc_ru : arch.desc_uk
            return (
              <button
                key={arch.id}
                type="button"
                onClick={() => handleLoadArchetype(arch.id)}
                className={`p-3 rounded-2xl border text-left transition-all min-w-[210px] sm:min-w-[240px] 2xl:min-w-0 snap-start flex flex-col justify-between shrink-0 ${
                  isActive
                    ? 'bg-[#c8a96e]/20 border-[#c8a96e] shadow-lg shadow-[#c8a96e]/15 scale-[1.01]'
                    : 'bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.06]'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg shrink-0">{arch.icon}</span>
                  <span className="text-xs font-bold text-white leading-tight">{name}</span>
                </div>
                <p className="text-[10px] text-neutral-400 line-clamp-1 leading-snug">{desc}</p>
              </button>
            )
          })}
        </div>
      </div>

      {/* ── 3. THE 4 OCCASION SLOTS ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between text-xs px-1">
          <span className="font-semibold uppercase tracking-wider text-white text-xs">
            {lang === 'en'
              ? 'Wardrobe Slots (4 Occasions)'
              : lang === 'ru'
                ? 'Слоты гардероба (4 повода)'
                : 'Ольфакторні слоти (4 життєві ситуації)'}
          </span>
          <span className="text-[11px] text-[#c9a24b] font-medium">
            {lang === 'en'
              ? 'Click Replace to customize each scent'
              : lang === 'ru'
                ? 'Нажмите Заменить для настройки'
                : 'Натисніть "Замінити" для вибору іншого парфуму'}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-4">
          {occasions.map((occ) => {
            const currentProd = currentSelected[occ.id] || occ.candidates[0]
            const occName = lang === 'en' ? occ.name_en : lang === 'ru' ? occ.name_ru : occ.name_uk
            const occDesc = lang === 'en' ? occ.desc_en : lang === 'ru' ? occ.desc_ru : occ.desc_uk

            return (
              <div
                key={occ.id}
                className="relative rounded-2xl bg-[#181622] border border-white/10 p-4 flex flex-col justify-between shadow-xl transition hover:border-[#c8a96e]/40 overflow-hidden"
              >
                {/* Slot Occasion Header: Occasion Icon + Title + Match Badge */}
                <div className="pb-3 mb-3 border-b border-white/10 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-xl shrink-0">{occ.icon}</span>
                    <div className="min-w-0">
                      <span className="text-sm font-bold text-white block truncate leading-tight">
                        {occName}
                      </span>
                      <span className="text-[11px] text-neutral-400 block truncate leading-tight mt-0.5">
                        {occDesc}
                      </span>
                    </div>
                  </div>
                  {currentProd && (
                    <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[11px] font-bold font-mono shrink-0">
                      {currentProd.match_pct}% {lang === 'en' ? 'match' : 'збіг'}
                    </span>
                  )}
                </div>

                {/* Scent Card Body */}
                {currentProd ? (
                  <div className="space-y-3.5 flex-1 flex flex-col justify-between">
                    <div className="flex items-center gap-3.5">
                      {/* Perfume Bottle Image with subtle ambient glow */}
                      <div className="relative shrink-0">
                        {currentProd.photo_url ? (
                          <img
                            src={currentProd.photo_url}
                            alt={currentProd.name_uk}
                            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover bg-neutral-900 border border-white/10 shadow-md"
                          />
                        ) : (
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-neutral-900 border border-white/10 flex items-center justify-center shadow-md">
                            <Package className="w-7 h-7 text-[#d7b171]/70" />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1 space-y-1">
                        {currentProd.brand && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#d7b171] block truncate">
                            {currentProd.brand}
                          </span>
                        )}
                        <h4 className="text-sm font-bold text-white line-clamp-2 leading-snug">
                          {lang === 'en' ? currentProd.name_en : currentProd.name_uk}
                        </h4>
                        <div className="pt-0.5">
                          <span className="inline-block px-2.5 py-0.5 rounded-md bg-[#c8a96e]/15 border border-[#c8a96e]/30 text-[#ebd5ab] text-xs font-mono font-bold">
                            5 мл • {currentProd.decant_price_uah} ₴
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Dominant Chords (up to 3 neat pills) */}
                    <div className="flex flex-wrap gap-1.5">
                      {currentProd.dominant_chords.slice(0, 3).map((ch, i) => (
                        <span
                          key={i}
                          className="px-2.5 py-1 rounded-lg bg-white/[0.04] text-[11px] text-neutral-300 border border-white/5 font-medium"
                        >
                          {ch}
                        </span>
                      ))}
                    </div>

                    {/* Slot Actions */}
                    <div className="flex items-center gap-2 pt-2.5 border-t border-white/5">
                      <button
                        type="button"
                        onClick={() => {
                          haptic('light')
                          setActiveReplacingOccasion(occ.id)
                          setCandidateSearch('')
                        }}
                        className="flex-1 py-2.5 px-3 rounded-xl bg-white/[0.06] hover:bg-[#c8a96e]/20 hover:border-[#c8a96e]/40 border border-white/10 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition active:scale-95 shadow-sm"
                      >
                        <RefreshCw className="w-3.5 h-3.5 text-[#d7b171]" />
                        <span>{lang === 'en' ? 'Replace Scent' : lang === 'ru' ? 'Заменить аромат' : 'Замінити аромат'}</span>
                      </button>
                      <Link
                        to="/catalog/$productSlug"
                        params={{ productSlug: currentProd.slug || String(currentProd.id) }}
                        className="p-2.5 rounded-xl bg-white/[0.05] hover:bg-white/10 text-neutral-400 hover:text-white transition shrink-0"
                        title="Open product details"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── 4. WARDROBE BALANCE & COMBINED 5D RADAR ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Wardrobe Balance Gauge */}
        <div className="p-4 rounded-2xl bg-[#181622] border border-white/10 space-y-3 flex flex-col justify-between shadow-xl">
          <div className="space-y-1.5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#d7b171] min-w-0">
                <ShieldCheck className="w-4 h-4 text-[#d7b171] shrink-0" />
                <span className="leading-snug">
                  {lang === 'en'
                    ? 'Wardrobe Orthogonality & Balance'
                    : lang === 'ru'
                      ? 'Баланс и ортогональность гардероба'
                      : 'Баланс & Ортогональність'}
                </span>
              </div>
              <span className="px-2 py-0.5 rounded-lg bg-[#c8a96e]/15 border border-[#c8a96e]/30 text-sm font-mono font-bold text-[#ebd5ab] shrink-0">
                {currentDiversity.score}%
              </span>
            </div>
            <p className="text-[11px] text-neutral-400 leading-relaxed">
              {lang === 'en' ? currentDiversity.label_en : currentDiversity.label_uk}
            </p>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                currentDiversity.score >= 85
                  ? 'bg-gradient-to-r from-amber-400 to-emerald-400'
                  : currentDiversity.score >= 70
                    ? 'bg-gradient-to-r from-blue-400 to-amber-400'
                    : 'bg-red-400'
              }`}
              style={{ width: `${currentDiversity.score}%` }}
            />
          </div>

          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 text-[11px] text-neutral-300 space-y-1">
            <span className="font-semibold text-white block">
              {lang === 'en'
                ? 'Why orthogonality matters?'
                : lang === 'ru'
                  ? 'Почему важна ортогональность?'
                  : 'Чому ортогональність важлива?'}
            </span>
            <p className="text-neutral-400 leading-normal">
              {lang === 'en'
                ? 'Each scent occupies a completely distinct sector of the 5D sensory hypercube: fresh citrus for focus, seductive amber for galas, cozy vanilla for rainy days, and crisp ozone for sports.'
                : lang === 'ru'
                  ? 'Каждый аромат занимает противоположный сектор в 5D пространстве: свежесть для офиса, амбра для свиданий, ваниль для уюта и озон для спорта.'
                  : 'Кожен аромат займає свій сектор у 5D просторі: свіжість для офісу, гаряча амбра для вечора, гурманська ваніль для дощу та акватичний озон для вікенду.'}
            </p>
          </div>
        </div>

        {/* Combined 5D Radar Visualizer */}
        <div className="p-4 rounded-2xl bg-[#181622] border border-white/10 space-y-2 shadow-xl flex flex-col items-center">
          <div className="w-full flex items-center justify-between text-xs mb-1">
            <span className="font-semibold uppercase tracking-wider text-neutral-300 text-[11px]">
              {lang === 'en'
                ? 'Combined 5D Radar (All 4 Scents)'
                : lang === 'ru'
                  ? 'Совмещенный 5D Радар (4 аромата)'
                  : 'Суміщений 5D Радар (Усі 4 аромати)'}
            </span>
            <span className="text-[10px] text-neutral-400">
              {lang === 'en' ? 'Full spectrum coverage' : 'Повне покриття спектру'}
            </span>
          </div>

          {/* SVG 5D Radar with 4 overlaid polygons */}
          <div className="relative aspect-square w-full max-w-[240px] flex items-center justify-center">
            <svg viewBox="0 0 200 200" className="w-full h-full overflow-visible">
              {/* Concentric Web Rings */}
              {[0.25, 0.5, 0.75, 1.0].map((scale, i) => {
                const points = [0, 1, 2, 3, 4]
                  .map((idx) => {
                    const ang = (idx * Math.PI * 2) / 5 - Math.PI / 2
                    const r = 70 * scale
                    return `${100 + Math.cos(ang) * r},${100 + Math.sin(ang) * r}`
                  })
                  .join(' ')
                return (
                  <polygon
                    key={i}
                    points={points}
                    fill="none"
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth="1"
                  />
                )
              })}

              {/* Axis Spoke Lines */}
              {[0, 1, 2, 3, 4].map((idx) => {
                const ang = (idx * Math.PI * 2) / 5 - Math.PI / 2
                return (
                  <line
                    key={idx}
                    x1="100"
                    y1="100"
                    x2={100 + Math.cos(ang) * 70}
                    y2={100 + Math.sin(ang) * 70}
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth="1"
                  />
                )
              })}

              {/* Overlaid Fragrance Polygons */}
              {occasions.map((occ) => {
                const prod = currentSelected[occ.id]
                if (!prod) return null
                const axes: Array<keyof typeof prod.vector> = [
                  'freshness',
                  'sharpness',
                  'sillage',
                  'sweetness',
                  'formality',
                ]
                const polyPoints = axes
                  .map((ax, idx) => {
                    const ang = (idx * Math.PI * 2) / 5 - Math.PI / 2
                    const val = Math.max(0.1, Math.min(0.95, prod.vector[ax] ?? 0.5))
                    const r = 70 * val
                    return `${100 + Math.cos(ang) * r},${100 + Math.sin(ang) * r}`
                  })
                  .join(' ')

                return (
                  <polygon
                    key={occ.id}
                    points={polyPoints}
                    fill={`${occ.color}25`}
                    stroke={occ.color}
                    strokeWidth="1.8"
                  />
                )
              })}
            </svg>
          </div>

          {/* Color Legend */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-1 text-[10px]">
            {occasions.map((occ) => (
              <span key={occ.id} className="flex items-center gap-1.5 text-neutral-300">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: occ.color }} />
                <span>{occ.icon} {lang === 'en' ? occ.name_en.split(' ')[0] : occ.name_uk.split(' ')[0]}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── 5. DISCOVERY BOX 1-CLICK ORDER CARD ── */}
      <div className="rounded-3xl bg-gradient-to-r from-[#201c2b] via-[#1a1724] to-[#201c2b] border border-[#c8a96e]/40 p-5 md:p-6 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-5">
        <div className="space-y-1.5 text-center md:text-left w-full md:w-auto">
          <div className="flex items-center justify-center md:justify-start gap-2">
            <span className="px-2.5 py-0.5 rounded-full bg-[#c8a96e]/20 text-[#ebd5ab] border border-[#c8a96e]/30 text-[11px] font-bold uppercase tracking-wider">
              {lang === 'en' ? 'Discovery Box Set' : 'Сет розпивів 4×5 мл'}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[11px] font-bold">
              {lang === 'en' ? 'Save 15%' : 'Економія 15%'}
            </span>
          </div>
          <h3 className="text-xl font-bold text-white font-serif">
            {lang === 'en'
              ? 'Complete Capsule Discovery Box (4 × 5 ml = 20 ml)'
              : 'Повний набір капсули Discovery Box (4 × 5 мл = 20 мл)'}
          </h3>
          <p className="text-xs text-neutral-400">
            {lang === 'en'
              ? 'Includes luxury travel atomizers, gift packaging, and personal fragrance sommelier guide.'
              : 'Включає фірмові скляні спрей-атомайзери 5 мл з металевими ковпачками та коробку.'}
          </p>
        </div>

        <div className="w-full md:w-auto flex flex-col sm:flex-row items-center justify-between md:justify-end gap-4 shrink-0">
          <div className="flex sm:flex-col items-center sm:items-end justify-between w-full sm:w-auto gap-2 sm:gap-0">
            <span className="text-xs text-neutral-500 line-through">
              {formatPrice(regularTotal)}
            </span>
            <span className="text-2xl font-bold font-serif text-[#ebd5ab]">
              {formatPrice(bundlePrice)}
            </span>
            <span className="text-[10px] text-emerald-400 font-medium">
              -{formatPrice(savings)}
            </span>
          </div>

          <button
            type="button"
            disabled={isAddingToCart}
            onClick={handleOrderDiscoveryBox}
            className="w-full sm:w-auto px-6 py-3.5 rounded-2xl bg-gradient-to-r from-[#c8a96e] to-[#d4af37] text-[#110f18] text-sm font-bold flex items-center justify-center gap-2 shadow-xl shadow-[#c8a96e]/20 transition hover:brightness-110 active:scale-95 disabled:opacity-50"
          >
            {isAddingToCart ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 fill-current" />
            )}
            <span>
              {lang === 'en'
                ? 'Order Discovery Box 4×5 ml'
                : lang === 'ru'
                  ? 'Заказать Discovery Box 4×5 мл'
                  : 'Замовити Discovery Box 4×5 мл'}
            </span>
          </button>
        </div>
      </div>

      {/* ── 6. CANDIDATE SELECTION MODAL (SLOT REPLACEMENT) ── */}
      {activeReplacingOccasion && activeOccasionData && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-xl max-h-[85vh] bg-[#181622] border border-[#c8a96e]/30 rounded-t-3xl sm:rounded-3xl p-5 space-y-4 flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{activeOccasionData.icon}</span>
                <div>
                  <h3 className="text-sm font-bold text-white">
                    {lang === 'en'
                      ? `Select scent for ${activeOccasionData.name_en}`
                      : `Оберіть аромат для: ${activeOccasionData.name_uk}`}
                  </h3>
                  <p className="text-[11px] text-neutral-400">
                    {lang === 'en' ? activeOccasionData.desc_en : activeOccasionData.desc_uk}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveReplacingOccasion(null)}
                className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                value={candidateSearch}
                onChange={(e) => setCandidateSearch(e.target.value)}
                placeholder={
                  lang === 'en' ? 'Search matching fragrance...' : 'Шукати відповідний аромат...'
                }
                className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-xs text-white placeholder-neutral-500 focus:outline-none focus:border-[#c8a96e]"
              />
            </div>

            {/* Candidates List */}
            <div className="overflow-y-auto space-y-2 flex-1 pr-1 scrollbar-thin">
              {activeOccasionData.candidates
                .filter((c) => {
                  if (!candidateSearch.trim()) return true
                  const q = candidateSearch.toLowerCase()
                  return (
                    c.name_uk.toLowerCase().includes(q) ||
                    (c.name_en && c.name_en.toLowerCase().includes(q)) ||
                    c.brand.toLowerCase().includes(q)
                  )
                })
                .map((cand) => {
                  const isCurrent = currentSelected[activeOccasionData.id]?.id === cand.id
                  return (
                    <div
                      key={cand.id}
                      className={`p-3 rounded-xl border flex items-center justify-between gap-3 transition ${
                        isCurrent
                          ? 'bg-[#c8a96e]/20 border-[#c8a96e]'
                          : 'bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.05]'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {cand.photo_url ? (
                          <img
                            src={cand.photo_url}
                            alt=""
                            className="w-11 h-11 rounded-lg object-cover bg-neutral-900 shrink-0"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-lg bg-neutral-900 flex items-center justify-center shrink-0">
                            <Package className="w-5 h-5 text-neutral-500" />
                          </div>
                        )}
                        <div className="min-w-0">
                          {cand.brand && (
                            <span className="text-[10px] text-[#d7b171] uppercase font-bold block truncate">
                              {cand.brand}
                            </span>
                          )}
                          <h4 className="text-xs font-bold text-white truncate">
                            {lang === 'en' ? cand.name_en : cand.name_uk}
                          </h4>
                          <span className="text-[10px] text-emerald-400 font-semibold">
                            {cand.match_pct}% {lang === 'en' ? 'match' : 'збіг'} • 5 мл (
                            {cand.decant_price_uah} ₴)
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleSelectProductForSlot(activeOccasionData.id, cand)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold shrink-0 transition ${
                          isCurrent
                            ? 'bg-emerald-500 text-black'
                            : 'bg-[#c8a96e] text-[#110f18] hover:bg-[#d4af37]'
                        }`}
                      >
                        {isCurrent ? (
                          <span className="flex items-center gap-1">
                            <Check className="w-3.5 h-3.5" />
                            <span>{lang === 'en' ? 'Active' : 'Обрано'}</span>
                          </span>
                        ) : (
                          <span>{lang === 'en' ? 'Choose' : 'Обрати'}</span>
                        )}
                      </button>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      )}

      {/* ── 7. CART SUCCESS CONFIRMATION MODAL ── */}
      {cartSuccessOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-[#181622] border border-[#c8a96e] rounded-3xl p-6 text-center space-y-4 shadow-2xl">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-white font-serif">
                {lang === 'en'
                  ? 'Discovery Box added to cart!'
                  : 'Discovery Box успішно додано в кошик!'}
              </h3>
              <p className="text-xs text-neutral-400">
                {lang === 'en'
                  ? '4 curated 5ml decants with 15% discount are ready for checkout.'
                  : '4 збалансовані розпиви по 5 мл зі знижкою 15% готові до оформлення.'}
              </p>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setCartSuccessOpen(false)}
                className="flex-1 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition"
              >
                {lang === 'en' ? 'Continue' : 'Продовжити'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCartSuccessOpen(false)
                  void navigate({ to: '/cart' })
                }}
                className="flex-1 py-2.5 rounded-xl bg-[#c8a96e] text-[#110f18] text-xs font-bold hover:bg-[#d4af37] transition"
              >
                {lang === 'en' ? 'Go to Cart' : 'Перейти в кошик'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
